/**
 * The preferences' one store, shared by every editor on the page or in the
 * app: read through `useFloorPlanPreferences`, which re-renders when they
 * change, and changed through `updateFloorPlanPreferences`. Each platform
 * keeps the object in its own storage, one serialized JSON string, by calling
 * `setFloorPlanPreferencesStorage` once before the first editor mounts. Until
 * then (and in tests and server rendering) they live in memory.
 */
import { useSyncExternalStore } from 'react';
import { type FloorPlanPreferences, parseFloorPlanPreferences } from './floorPlanPreferences';

/** Where a platform keeps the preferences: the one serialized string, null when nothing is stored. */
export interface FloorPlanPreferencesStorage {
  read(): string | null;
  write(serialized: string): void;
  /** Tells the store of a change made outside it (another tab, another writer); the store adopts it. */
  listen?(onExternalChange: (serialized: string | null) => void): void;
}

const memoryStorage = (): FloorPlanPreferencesStorage => {
  let stored: string | null = null;
  return { read: () => stored, write: serialized => void (stored = serialized) };
};

let storage = memoryStorage();
let serializedInStorage: string | null = null;
let current = parseFloorPlanPreferences(null);
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach(listener => listener());

/** The stored string; storage that cannot be read (blocked site data) holds nothing. */
function readStorage(): string | null {
  try {
    return storage.read();
  } catch {
    return null;
  }
}

/** Takes `serialized` as the preferences and tells the readers. */
function takeStored(serialized: string | null): void {
  serializedInStorage = serialized;
  current = parseFloorPlanPreferences(serialized);
  notify();
}

/** A change made outside the store; what the store itself wrote is not one. */
function adoptExternalChange(serialized: string | null): void {
  if (serialized !== serializedInStorage) takeStored(serialized);
}

/** Keeps the preferences in `platformStorage` from now on, starting with what it holds. Called once by each platform, before the first editor mounts. */
export function setFloorPlanPreferencesStorage(platformStorage: FloorPlanPreferencesStorage): void {
  storage = platformStorage;
  takeStored(readStorage());
  platformStorage.listen?.(adoptExternalChange);
}

export const getFloorPlanPreferences = (): FloorPlanPreferences => current;

/** Changes the given preferences; a write storage refuses (full, blocked) still holds for the session. */
export function updateFloorPlanPreferences(patch: Partial<FloorPlanPreferences>): void {
  current = { ...current, ...patch };
  serializedInStorage = JSON.stringify(current);
  try {
    storage.write(serializedInStorage);
  } catch {
    // storage full or blocked: the preference still holds for the session
  }
  notify();
}

const subscribe = (listener: () => void): () => void => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The current preferences; the component re-renders when they change. */
export const useFloorPlanPreferences = (): FloorPlanPreferences => useSyncExternalStore(subscribe, getFloorPlanPreferences, getFloorPlanPreferences);
